import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Globe, DeviceMobile, Desktop, DeviceTablet, WifiHigh } from '@phosphor-icons/react';

const DEVICE_ICON = { mobile: DeviceMobile, tablet: DeviceTablet, desktop: Desktop };

function relativeTime(ts) {
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function formatClock(ts) {
  try {
    return new Date(ts).toLocaleTimeString(undefined, {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '—';
  }
}

function refererHost(raw) {
  if (!raw || raw === 'direct') return '(direct)';
  try {
    const u = String(raw).includes('://') ? new URL(raw) : new URL(`https://${raw}`);
    return u.hostname.replace(/^www\./, '') || String(raw).slice(0, 48);
  } catch {
    return String(raw).slice(0, 48);
  }
}

function deviceLabel(click) {
  const raw = (click.device_type || 'desktop').toLowerCase();
  const cap = raw.charAt(0).toUpperCase() + raw.slice(1);
  if (click.browser) return `${cap} · ${click.browser}`;
  return cap;
}

function ClickRowDefault({ click, fresh }) {
  const DevIcon = DEVICE_ICON[click.device_type] || Desktop;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs transition-all duration-500 ${
        fresh ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/40'
      }`}
    >
      <span className="flex-shrink-0 grid place-items-center h-7 w-7 rounded-lg bg-muted">
        <Globe size={14} className="text-muted-foreground" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-foreground truncate">
          {click.country || 'Unknown'}
          {click.city && click.city !== 'Unknown' ? ` · ${click.city}` : ''}
        </div>
        <div className="text-muted-foreground truncate">
          {click.referrer && click.referrer !== 'direct' ? click.referrer : 'Direct visit'}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0 text-muted-foreground">
        <DevIcon size={13} />
        <span>{relativeTime(click.timestamp)}</span>
      </div>
    </div>
  );
}

function ClickRowMock({ click, fresh }) {
  const host = refererHost(click.referrer);
  return (
    <div className={`lfld-live-row ${fresh ? '' : 'lfld-live-row--older'}`}>
      <div className="lfld-live-dot-cell" aria-hidden />
      <div className="lfld-live-time">
        {formatClock(click.timestamp)}
        <span className="rel">{relativeTime(click.timestamp)}</span>
      </div>
      <div className="min-w-0">
        <div className="lfld-live-event-text">Click recorded</div>
        <div className="lfld-live-event-sub">via {host}</div>
      </div>
      <div className="min-w-0">
        <div className="lfld-live-event-text">
          {[click.country || 'Unknown', click.city && click.city !== 'Unknown' ? click.city : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      <div className="lfld-live-device">{deviceLabel(click)}</div>
    </div>
  );
}

export default function LiveClickFeed({ linkId, workspaceId, maxItems = 10, layout = 'default' }) {
  const [clicks, setClicks] = useState([]);
  const [connected, setConnected] = useState(false);
  const freshIds = useRef(new Set());

  useEffect(() => {
    let channel;

    async function bootstrap() {
      let query = supabase
        .from('clicks')
        .select('click_id, country, city, device_type, browser, referrer, timestamp')
        .eq('is_bot', false)
        .order('timestamp', { ascending: false })
        .limit(maxItems);

      if (linkId) query = query.eq('link_id', linkId);
      else if (workspaceId) query = query.eq('workspace_id', workspaceId);

      const { data } = await query;
      if (data) setClicks(data);

      const filter = linkId ? `link_id=eq.${linkId}` : `workspace_id=eq.${workspaceId}`;

      channel = supabase
        .channel(`live-clicks-${linkId || workspaceId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'clicks',
            filter,
          },
          (payload) => {
            const click = payload.new;
            if (click.is_bot) return;
            freshIds.current.add(click.click_id);
            setClicks((prev) => [click, ...prev].slice(0, maxItems));
            setTimeout(() => {
              freshIds.current.delete(click.click_id);
              setClicks((c) => [...c]);
            }, 3000);
          }
        )
        .subscribe((status) => {
          setConnected(status === 'SUBSCRIBED');
        });
    }

    bootstrap();
    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [linkId, workspaceId, maxItems]);

  const Row = layout === 'mock' ? ClickRowMock : ClickRowDefault;
  const wrapClass = layout === 'mock' ? 'lfld-live-feed' : 'space-y-1';

  return (
    <div className={wrapClass}>
      {layout === 'mock' ? (
        <div className="lfld-live-header">
          <span className={`lfld-live-badge ${connected ? 'lfld-live-badge-on' : 'lfld-live-badge-off'}`}>
            {connected ? <span className="lfld-tab-live-dot" aria-hidden /> : (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--ink-dim)',
                  display: 'inline-block',
                }}
              />
            )}
            {connected ? 'LIVE' : 'Connecting…'}
          </span>
          <span>
            {clicks.length === 0 ? 'Waiting for clicks…' : `${clicks.length} event${clicks.length === 1 ? '' : 's'}`}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
              connected ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                connected ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'
              }`}
            />
            {connected ? 'LIVE' : 'Connecting…'}
          </span>
          <span className="text-xs text-muted-foreground">
            {clicks.length === 0 ? 'Waiting for clicks…' : `Last ${clicks.length} clicks`}
          </span>
        </div>
      )}

      {clicks.length === 0 ? (
        <div
          className={`flex flex-col items-center justify-center text-muted-foreground gap-2 ${
            layout === 'mock' ? 'py-12' : 'py-10'
          }`}
        >
          <WifiHigh size={28} className="opacity-30" />
          <span className="text-xs">No clicks in this period yet.</span>
        </div>
      ) : (
        clicks.map((c) => <Row key={c.click_id} click={c} fresh={freshIds.current.has(c.click_id)} />)
      )}
    </div>
  );
}
